import { useMutation, useQuery } from '@tanstack/react-query';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { customFetcher } from '../useFetch';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { IFSScanResult } from '../../Interfaces/IFSDirectory';
import { IFSDirectorySortedDocument } from '../../Interfaces/IFSDirectorySortedDocument';
import { IDocument } from '../../Interfaces/IDocument';
import { IDocumentAnalysisSection } from '../../Interfaces/IDocumentAnalysisSection';
import { IDocumentAnalysisSubsection } from '../../Interfaces/IDocumentAnalysisSubsection';
import { IDocumentAnalysisAttribute } from '../../Interfaces/IDocumentAnalysisAttribute';
import { ISendDocumentNew } from '../../Interfaces/ISendDocumentNew';
import { IDocumentAnalysisType } from '../../Interfaces/IDocumentAnalysisType';

export const useExportPDF = () => {
  const { mutateAsync: exportPDF, isPending: isPendingExportPDF } = useMutation(
    {
      mutationFn: (uploadObject: ILegalTransactionFullObject) =>
        customFetcher({
          endpoint: '/legaltransaction/export/pdf',
          body: uploadObject,
          method: 'POST',
        }),
    }
  );

  return { exportPDF, isPendingExportPDF };
};

export const useGetDownloadedDocuments = () => {
  const {
    data: downloadedDocuments,
    isLoading: isLoadingDownloadedDocuments,
    refetch: refetchDownloadedDocuments,
  } = useQuery<IDocumentDownloaded[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentdownloaded'],
    queryFn: () => customFetcher({ endpoint: '/documentdownloaded' }),
  });

  return {
    downloadedDocuments,
    isLoadingDownloadedDocuments,
    refetchDownloadedDocuments,
  };
};

export const useSendDocuments = () => {
  const { mutateAsync: sendDocuments, isPending: isPendingSendDocuments } =
    useMutation({
      mutationFn: (uploadObject: ISendDocumentNew) =>
        customFetcher({
          endpoint: '/legaltransaction/export/sendDocuments',
          body: uploadObject,
          method: 'POST',
        }),
    });

  return { sendDocuments, isPendingSendDocuments };
};

export const useGetFSSortedDocumentsWithFilter = (
  fsScanResults: IFSScanResult[]
) => {
  const fsDirectoryIds = Array.from(
    new Set(fsScanResults.map(el => el.idFSDirectory))
  ).join(',');

  let endpoint = `/fsdirectorysorteddocument/getWithFilter?directoryIds=${fsDirectoryIds}&filenames=${fsScanResults.map(el => el.fileName).join(',')}`;

  if (!fsScanResults || fsScanResults.length === 0) {
    endpoint = `${endpoint}/-1`;
  }

  const {
    data: fsSortedDocuments,
    isLoading: isLoadingFSSortedDocuments,
    refetch: refetchFSSortedDocuments,
  } = useQuery<IFSDirectorySortedDocument[]>({
    queryKey: ['fsdirectorysorteddocument/getWithFilter', endpoint],
    queryFn: () =>
      customFetcher({
        endpoint,
        method: 'GET',
      }),
  });
  return {
    fsSortedDocuments,
    isLoadingFSSortedDocuments,
    refetchFSSortedDocuments,
  };
};

export const useGetFSDocument = (fsScanResult: IFSScanResult) => {
  const {
    data: fsDocument,
    isLoading: isLoadingFSDocument,
    refetch: refetchFSDocument,
  } = useQuery<IDocument>({
    queryKey: ['fsdirectory/getDoc', fsScanResult],
    queryFn: () =>
      customFetcher({
        endpoint: '/fsdirectory/getDoc',
        body: fsScanResult,
        method: 'POST',
      }),
  });

  return { fsDocument, isLoadingFSDocument, refetchFSDocument };
};

export const useSortFSDirectoryDocument = () => {
  const {
    mutateAsync: sortFSDirectoryDocument,
    isPending: isSortingFSDirectoryDocument,
  } = useMutation({
    mutationFn: (sortedDocument: IFSDirectorySortedDocument) =>
      customFetcher({
        endpoint: '/fsdirectorysorteddocument',
        body: sortedDocument,
        method: 'POST',
      }),
  });

  return { sortFSDirectoryDocument, isSortingFSDirectoryDocument };
};

export const useGetDocument = ({
  idDocument,
  enabled = true,
}: {
  idDocument: number;
  enabled?: boolean;
}) => {
  const {
    data: document,
    isLoading: isLoadingDocument,
    refetch: refetchDocument,
  } = useQuery<IDocument>({
    queryKey: ['document', idDocument],
    queryFn: () => customFetcher({ endpoint: `/document/${idDocument}` }),
    enabled,
  });

  return { document, isLoadingDocument, refetchDocument };
};

//////////////////// DocumentAnalysis
export const useDocumentAnalysisTypes = () => {
  const {
    data: documentAnalysisTypes,
    isLoading: isLoadingDocumentAnalysisTypes,
  } = useQuery<IDocumentAnalysisType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisType'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysistype' }),
  });

  return { documentAnalysisTypes, isLoadingDocumentAnalysisTypes };
};

export const useDocumentAnalysisSections = () => {
  const {
    data: documentAnalysisSections,
    isLoading: isLoadingDocumentAnalysisSections,
  } = useQuery<IDocumentAnalysisSection[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisSection'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysissection' }),
  });

  return { documentAnalysisSections, isLoadingDocumentAnalysisSections };
};

export const useDocumentAnalysisSubsections = () => {
  const {
    data: documentAnalysisSubsections,
    isLoading: isLoadingDocumentAnalysisSubsections,
  } = useQuery<IDocumentAnalysisSubsection[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisSubsection'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysissubsection' }),
  });

  return { documentAnalysisSubsections, isLoadingDocumentAnalysisSubsections };
};

export const useDocumentAnalysisAttributes = () => {
  const {
    data: documentAnalysisAttributes,
    isLoading: isLoadingDocumentAnalysisAttributes,
  } = useQuery<IDocumentAnalysisAttribute[]>({
    refetchOnWindowFocus: false,
    queryKey: ['documentAnalysisAttribute'],
    queryFn: () => customFetcher({ endpoint: '/documentanalysisattribute' }),
  });

  return { documentAnalysisAttributes, isLoadingDocumentAnalysisAttributes };
};
